import { useEffect } from "react";

interface StructuredDataProps {
  type?:
    | "organization"
    | "website"
    | "service"
    | "article"
    | "faq"
    | "breadcrumb";
  data?: any;
}

const StructuredData = ({
  type = "organization",
  data,
}: StructuredDataProps) => {
  useEffect(() => {
    let structuredData: any = {};

    switch (type) {
      case "organization":
        structuredData = {
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Smartborn",
          alternateName: "Smartborn IT Services",
          url: window.location.origin,
          logo: {
            "@type": "ImageObject",
            url: `${window.location.origin}/og-image.png`,
            width: 1200,
            height: 630,
            caption: "Smartborn - Enterprise AI & IT Services",
          },
          description:
            "Leading enterprise AI & IT services company delivering innovative software solutions, offshore development, and staff augmentation. 15+ years experience across 25+ industries.",
          foundingDate: "2010",
          slogan:
            "Build, Automate & Scale With Enterprise-Grade AI & IT Services",
          numberOfEmployees: {
            "@type": "QuantitativeValue",
            value: "200+",
          },
          industry: "Information Technology Services",
          knowsAbout: [
            "Artificial Intelligence",
            "Machine Learning",
            "Custom Software Development",
            "Cloud Computing",
            "Cybersecurity",
            "DevOps",
            "Mobile App Development",
            "Enterprise Software",
            "SAP Consulting",
            "Salesforce Implementation",
            "Workday HCM",
          ],
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+1-800-SMARTBORN",
            contactType: "customer service",
            email: "contact@smartborn.io",
            availableLanguage: ["English"],
            areaServed: "Worldwide",
            hoursAvailable: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
                "Sunday",
              ],
              opens: "00:00",
              closes: "23:59",
            },
          },
          address: {
            "@type": "PostalAddress",
            streetAddress: "123 Innovation Drive",
            addressLocality: "Tech City",
            addressRegion: "CA",
            postalCode: "12345",
            addressCountry: "US",
          },
          sameAs: [
            "https://linkedin.com/company/smartborn",
            "https://twitter.com/smartborn",
            "https://facebook.com/smartborn",
            "https://github.com/smartborn",
            "https://instagram.com/smartborn",
          ],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "150",
            bestRating: "5",
            worstRating: "1",
          },
          offers: {
            "@type": "Service",
            serviceType: "Enterprise IT Services",
            description:
              "Comprehensive IT solutions including custom software development, AI/ML, mobile app development, cloud migration, cybersecurity, DevOps automation, and staff augmentation services",
            provider: {
              "@type": "Organization",
              name: "Smartborn",
            },
            areaServed: {
              "@type": "Place",
              name: "Worldwide",
            },
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "IT Services Catalog",
              itemListElement: [
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Custom Software Development",
                    description:
                      "Tailored enterprise software solutions built from scratch",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "AI & Machine Learning Development",
                    description:
                      "Advanced AI and ML solutions for business automation",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Mobile App Development",
                    description:
                      "Native and cross-platform mobile applications",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Cloud Migration & Solutions",
                    description:
                      "Enterprise cloud migration and infrastructure management",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Staff Augmentation",
                    description:
                      "Scale your development team with skilled professionals",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Cybersecurity Services",
                    description:
                      "Comprehensive security solutions and risk management",
                  },
                },
              ],
            },
          },
        };
        break;

      case "website":
        structuredData = {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Smartborn",
          url: window.location.origin,
          description:
            "Leading IT services company delivering innovative software solutions",
          publisher: {
            "@type": "Organization",
            name: "Smartborn",
          },
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate: `${window.location.origin}/search?q={search_term_string}`,
            },
            "query-input": "required name=search_term_string",
          },
        };
        break;

      case "service":
        structuredData = {
          "@context": "https://schema.org",
          "@type": "Service",
          name: data?.name || "IT Services",
          description:
            data?.description || "Professional IT services and solutions",
          provider: {
            "@type": "Organization",
            name: "Smartborn",
            url: window.location.origin,
          },
          areaServed: "Worldwide",
          serviceType: data?.serviceType || "Information Technology",
          offers: {
            "@type": "Offer",
            availability: "https://schema.org/InStock",
            businessFunction: "https://www.gs1.org/voc/Sell",
          },
        };
        break;

      case "faq":
        structuredData = {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity:
            data?.faqs?.map((faq: any) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })) || [],
        };
        break;

      case "breadcrumb":
        structuredData = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement:
            data?.breadcrumbs?.map((item: any, index: number) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.name,
              item: `${window.location.origin}${item.url}`,
            })) || [],
        };
        break;

      case "article":
        structuredData = {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: data?.headline || document.title,
          description:
            data?.description || "IT services and technology solutions",
          image: data?.image || `${window.location.origin}/og-image.png`,
          author: {
            "@type": "Organization",
            name: "Smartborn",
          },
          publisher: {
            "@type": "Organization",
            name: "Smartborn",
            logo: {
              "@type": "ImageObject",
              url: `${window.location.origin}/og-image.png`,
            },
          },
          datePublished: data?.datePublished || "2025-01-24",
          dateModified: data?.dateModified || "2025-01-24",
        };
        break;
    }

    // Create and inject the structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(structuredData);
    script.id = `structured-data-${type}`;

    // Remove existing script if it exists
    const existingScript = document.getElementById(`structured-data-${type}`);
    if (existingScript) {
      existingScript.remove();
    }

    document.head.appendChild(script);

    return () => {
      // Cleanup on unmount
      const scriptToRemove = document.getElementById(`structured-data-${type}`);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, data]);

  return null;
};

export default StructuredData;
